import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { ExternalLink, FileText, Inbox, Eye, Sparkles, ArrowRight, Settings, ShieldCheck, CreditCard, Printer, Check, X as XIcon } from "lucide-react";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  if (loading || !stats) {
    return <div className="container-cd py-20 overline">{t("common.loading")}</div>;
  }

  return (
    <div className="container-cd py-12" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="overline">{t("dash.welcome")} · {user?.name?.toUpperCase()}</div>
          <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[0.9]">
            {stats.company_name || user?.name}
          </h1>
          <div className="mt-2 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            TIER · {stats.subscription_tier.toUpperCase()} · STATUS · {stats.subscription_status.toUpperCase()}
          </div>
        </div>
        <div className="flex gap-3">
          {stats.published ? (
            <Link to={`/m/${stats.slug}`} className="btn btn-ghost" data-testid="view-public-btn">
              <ExternalLink size={14} /> {t("dash.view_public")}
            </Link>
          ) : null}
          {(stats.subscription_tier === "pro" || stats.subscription_tier === "premium") && (
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/subscriptions/portal");
                  if (data?.url) window.location.href = data.url;
                } catch (e) {
                  alert(e.response?.data?.detail || "Could not open billing portal");
                }
              }}
              className="btn btn-ghost"
              data-testid="manage-subscription-btn"
            >
              <CreditCard size={14} /> Manage subscription
            </button>
          )}
          <Link to="/profile" className="btn btn-primary" data-testid="edit-profile-btn">
            <Settings size={14} /> {t("profile.title")}
          </Link>
        </div>
      </div>

      {!stats.published && (
        <div
          className="cd-card p-6 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ borderColor: "var(--orange)" }}
          data-testid="unpublished-warning"
        >
          <div>
            <div className="overline" style={{ color: "var(--orange)" }}>UNPUBLISHED</div>
            <div className="mt-2 text-base">{t("dash.publish_warning")}</div>
          </div>
          <Link to="/profile" className="btn btn-primary">{t("dash.publish_cta")}</Link>
        </div>
      )}

      {/* iter30 — Verification fields progress (4 fields: corporate
       * email, phone, website, company name). Hides once the user
       * already has the green Verified badge. Each pill shows ✓ or ✗
       * so owners see exactly which field is still missing. */}
      {!stats.verified && stats.verification_fields ? (
        <VerificationProgress stats={stats} lang={lang} />
      ) : null}

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0" style={{ border: "1px solid var(--line)" }}>
        <Stat label={t("dash.profile_views")} value={stats.profile_views} testid="stat-views" />
        <Stat label={t("dash.inquiries")} value={stats.inquiries_new} sub={`/ ${stats.inquiries_total} total`} testid="stat-inquiries" />
        <Stat label={t("dash.documents")} value={stats.documents} testid="stat-docs" />
        <Stat label={t("dash.expiring")} value={stats.documents_expiring_soon} highlight={stats.documents_expiring_soon > 0} testid="stat-expiring" last />
      </div>

      {/* Completeness + actions */}
      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <div className="md:col-span-1 cd-card p-6">
          <div className="overline">{t("dash.completeness")}</div>
          <div className="font-display text-6xl mt-4">{stats.profile_completeness}%</div>
          <div className="mt-4 h-2" style={{ background: "var(--bg)", border: "1px solid var(--line)" }}>
            <div
              style={{
                width: `${stats.profile_completeness}%`,
                height: "100%",
                background: "var(--orange)",
              }}
            />
          </div>
          <Link to="/profile" className="btn btn-ghost btn-sm w-full mt-6">
            Finish profile <ArrowRight size={14} />
          </Link>
        </div>

        <div className="md:col-span-2 cd-card p-6">
          <div className="overline">{t("dash.actions")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <ShortcutLink to="/profile" icon={Sparkles} title={t("profile.title")} sub="Update the spec sheet" />
            <ShortcutLink to="/documents" icon={FileText} title={t("docs.title")} sub="IMMEX, USMCA, ISO…" />
            <ShortcutLink to="/inquiries" icon={Inbox} title={t("inq.title")} sub={`${stats.inquiries_new} new`} />
            <ShortcutLink to="/dashboard/letter" icon={Printer} title={t("dash.letterTitle")} sub={t("dash.letterSub")} testid="dashboard-letter-link" />
            <ShortcutLink to="/pricing" icon={ShieldCheck} title={t("nav.pricing")} sub={stats.subscription_tier === "free" ? "Upgrade to Pro" : "Manage plan"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, highlight, testid, last }) {
  return (
    <div
      className="p-6 md:p-8"
      style={{
        background: "white",
        borderRight: last ? "none" : "1px solid var(--line)",
      }}
      data-testid={testid}
    >
      <div className="overline">{label}</div>
      <div className="font-display text-5xl mt-3" style={{ color: highlight ? "var(--orange)" : "var(--ink)" }}>
        {value}
      </div>
      {sub ? <div className="font-mono text-xs mt-2" style={{ color: "var(--ink-2)" }}>{sub}</div> : null}
    </div>
  );
}

function ShortcutLink({ to, icon: Icon, title, sub, testid }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 p-4"
      style={{ border: "1px solid var(--line)" }}
      data-testid={testid}
    >
      <Icon size={20} />
      <div>
        <div className="font-heading font-semibold">{title}</div>
        <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>{sub}</div>
      </div>
      <ArrowRight size={16} className="ml-auto" />
    </Link>
  );
}

/* iter30 — Verification progress: bar + 4 field pills (corporate email,
 * phone, website, company name). Only rendered when the profile is NOT
 * already verified. Each pill shows a green check or red X so the owner
 * can spot exactly what's missing from earning the verified badge. */
function VerificationProgress({ stats, lang }) {
  const v = stats.verification_fields || {};
  const complete = stats.verification_complete ?? 0;
  const total = stats.verification_total ?? 4;
  const pct = Math.round((complete / total) * 100);

  const labels = lang === "es"
    ? {
        heading: "Verificación",
        progress: `${complete} de ${total} campos completos`,
        cta: "Completar perfil",
        corporate_email: "Correo corporativo",
        phone: "Teléfono",
        website: "Sitio web",
        company_name: "Nombre de la empresa",
      }
    : {
        heading: "Verification",
        progress: `${complete} of ${total} verification fields complete`,
        cta: "Complete profile",
        corporate_email: "Corporate email",
        phone: "Phone",
        website: "Website",
        company_name: "Company name",
      };

  const fields = [
    { key: "corporate_email", filled: !!v.corporate_email },
    { key: "phone", filled: !!v.phone },
    { key: "website", filled: !!v.website },
    { key: "company_name", filled: !!v.company_name },
  ];

  return (
    <div
      className="cd-card p-6 mb-8"
      style={{ borderColor: complete === total ? "var(--green)" : "var(--line)" }}
      data-testid="dashboard-verification-progress"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline" style={{ color: "var(--ink-2)" }}>{labels.heading}</div>
          <div
            className="font-display text-3xl mt-2 leading-none"
            data-testid="dashboard-verif-progress-text"
          >
            <span style={{ color: complete === total ? "var(--green)" : "var(--ink)" }}>
              {complete}
            </span>
            <span className="font-mono text-sm mx-2" style={{ color: "var(--ink-2)" }}>/</span>
            <span>{total}</span>
            <span className="font-mono text-sm ml-3" style={{ color: "var(--ink-2)" }}>
              {labels.progress}
            </span>
          </div>
        </div>
        <Link to="/profile" className="btn btn-primary btn-sm" data-testid="dashboard-verif-cta">
          {labels.cta} <ArrowRight size={14} />
        </Link>
      </div>

      {/* Progress bar */}
      <div
        className="mt-5 h-2"
        style={{ background: "var(--bg)", border: "1px solid var(--line)" }}
        data-testid="dashboard-verif-bar"
        data-pct={pct}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: complete === total ? "var(--green)" : "var(--orange)",
            transition: "width 0.4s ease",
          }}
        />
      </div>

      {/* Per-field pills */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
        {fields.map((f) => (
          <div
            key={f.key}
            className="border flex items-center gap-2 px-3 py-2 font-mono text-xs"
            style={{
              borderColor: f.filled ? "var(--green)" : "var(--line)",
              background: f.filled ? "rgba(22,163,74,0.05)" : "white",
            }}
            data-testid={`dashboard-verif-field-${f.key}`}
            data-filled={f.filled ? "1" : "0"}
          >
            {f.filled ? (
              <Check size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
            ) : (
              <XIcon size={14} style={{ color: "var(--ink-2)", flexShrink: 0 }} />
            )}
            <span style={{ color: f.filled ? "var(--ink)" : "var(--ink-2)" }}>
              {labels[f.key]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
